import { useLayoutEffect, useState } from 'preact/hooks';
import { utag } from 'src/ConsentManagement/Tealium/Tealium';
import * as ErrorLogger from 'src/ConsentManagement/Logging/ErrorLogger';
import { useLanguage } from 'src/Shared/EventPublisher/UseLanguage';
import { NullComponent } from 'src/ConsentManagement/ConsentApp';
import { JSXInternal } from 'preact/src/jsx';
import { LanguageSwitcherView } from '../LanguageSwitcherView';
import { Content } from './Content';
import { Footer } from './Footer';
import * as ConsentLogger from '../../Logging/ConsentLogger';
import { ContentService } from '../../ContentService';
import { Container } from '../shared/Container';
import { HasActiveVendorChanged } from '../../Services/VendorStorageService';


export const ExplicitPanel = () => {
  useLanguage(); //triggers react state on language change
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useLayoutEffect(() => {
    if (!isFirstLoad) return;
    setIsFirstLoad(false);
    new LanguageSwitcherView().build();
    ConsentLogger.logConsent(ConsentLogger.ConsentLevel.Shown);
  }, []);

  return (
    <Container sectionId={'explicit_consent'}>
      <Content />
      <Footer />
    </Container>
  );
};

const hasUserConsented = () => !!utag?.gdpr?.getConsentState();

const canConsentBeShown = (hasVendorChanged: boolean): boolean =>
  !isIframe() &&
  !utag?.gdpr?.consent_prompt.noShow &&
  utag?.gdpr?.consent_prompt.isEnabled &&
  !isMobile() &&
  (hasUserConsented() ? hasVendorChanged : true);

const isMobile = () => {
  const qs = utag?.data['dom.query_string'];
  if (!qs) return false;
  return qs.toString().toLowerCase().indexOf('notemplate=1'.toLowerCase()) >= 0;
};

const isIframe = () => {
  if (window.location.hostname.toLowerCase().indexOf('localhost') >= 0) return false; // continue showing for cypress tests

  const topWindowAccess = Object.keys(window.top.location).filter((key) => key === 'hostname').length > 0;

  if (topWindowAccess) {
    try {
      return window.top.location.hostname !== window.location.hostname;
    } catch (error) {
      // ie fix as topWindowAccess returns false positive for ie11
      return true;
    }
  }

  return true;
};

const getCookieValue = (cookieName: string) => {
  const cookieNameEquals = cookieName + "=";
  const ca = document.cookie.split(';');

  for (const c of ca) {
    const y = c.trim();
    if (y.indexOf(cookieNameEquals) == 0) return y.substring(cookieNameEquals.length, y.length);
  }
  return '';
}

const getParameterByName = (name: string, url = window.location.href) => {
  name = name.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)', 'i'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return null;
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

const isIJMJEBCampaignCode = (campaignCode: string): boolean =>
  ((campaignCode.indexOf("R_EM_JBE_") == 0) || (campaignCode.indexOf("R_EM_IJM_") == 0));

const softLoginABTest = () => {
  try {
    if (getCookieValue("SoftLoginCookie") == "") return true;

    const campaignCode = getParameterByName("WT.mc_id") ?? getParameterByName("DCMP");
    if (campaignCode == null || !isIJMJEBCampaignCode(campaignCode)) return true;

    setConsentMgrCookie();
    return false;
  }
  catch (error) {
    ErrorLogger.log(`Error for soft Login A/B Test`, error);       
    return true;
  }
};

const setConsentMgrCookie = () => {
  const cookieName = "CONSENTMGR";
  const cookieValue = `c1:0%7Cc2:0%7Cc3:0%7Cc4:0%7Cc5:0%7Cc6:0%7Cc7:0%7Cc8:0%7Cc9:1%7Cc10:0%7Cc11:0%7Cc12:0%7Cc13:0%7Cc14:0%7Cc15:0%7Cts:${new Date().valueOf()}%7Cconsent:true`;
  const expiryDate = new Date();
  expiryDate.setMonth(expiryDate.getMonth() + 1);
  document.cookie = `${cookieName}=${cookieValue}; expires=${expiryDate.toUTCString()}; domain=${utag.cfg.domain}; path=/;`;
}

export const RenderExplicitPanel = async (): Promise<() => JSXInternal.Element> => {
  const hasVendorChanged = await HasActiveVendorChanged();
  if (!canConsentBeShown(hasVendorChanged)) return NullComponent;
  if (!softLoginABTest()) return NullComponent;

  await ContentService.FetchActiveLanguage();

  return ExplicitPanel;
};
